<template>
  <v-form data-test-id="manageSlotsView" :fast-fail="false" @submit.prevent ref="manageSlotsForm">
    <v-btn-toggle
      class="toggle-button"
      v-model="store.mode"
      density="compact"
      color="primary"
      rounded="8"
      mandatory
      variant="outlined"
      @update:model-value="onToggleChange"
    >
      <v-btn :value="ManageSlotsMode.Create">{{ t('buttons.create') }}</v-btn>
      <v-btn :value="ManageSlotsMode.Clear">{{ t('buttons.clear') }}</v-btn>
    </v-btn-toggle>

    <v-container class="ma-1 pa-1" max-width="425px">
      <v-row style="margin-top: 0rem">
        <v-col cols="12">
          <PrimarySelect
            v-model="store.eventCategory"
            :label="t('labels.eventCategory')"
            :items="eventsStore.getCategoryOptions(store.mode === ManageSlotsMode.Clear)"
          ></PrimarySelect>
          <v-container class="ma-0 pa-0" max-width="425px">
            <v-row>
              <v-col cols="6">
                <DatePicker
                  required
                  v-model="store.startDate"
                  :label="t('labels.slots.from')"
                  :min="store.minStartDate"
                  :max="store.maxStartDate"
                />
              </v-col>
              <v-col cols="6">
                <DatePicker
                  required
                  v-model="store.endDate"
                  :label="t('labels.slots.to')"
                  :min="store.minEndDate"
                  :max="store.maxEndDate"
                />
              </v-col>
            </v-row>
          </v-container>
          <v-container>
            <v-row>
              <v-col cols="6" class="ma-0 pa-0">
                <TimePicker
                  v-model="store.startTime"
                  :date="store.startDate"
                  :label="t('labels.slots.startTime')"
                  :twelve-hour-clock="calendarStore.twelveHourClock"
                  :rules="RulesManager.getRules(Rule.ValidFutureTime)"
                />
              </v-col>
              <v-col cols="6" class="ma-0 pa-0">
                <TimePicker
                  v-model="store.endTime"
                  :label="t('labels.slots.endTime')"
                  :twelve-hour-clock="calendarStore.twelveHourClock"
                />
              </v-col>
            </v-row>
          </v-container>
          <v-container
            v-if="store.mode === ManageSlotsMode.Create"
            class="ma-0 pa-0"
            max-width="425px"
          >
            <v-row>
              <v-col cols="6">
                <TimePicker
                  v-model="store.duration"
                  :label="t('labels.slots.duration')"
                  :max-hours="8"
                  :duration-mode="true"
                />
              </v-col>
              <v-col cols="6" style="padding-top: 2rem"> &nbsp; </v-col>
            </v-row>
          </v-container>
          <span class="v-label" style="font-size: 0.75rem">{{ t('labels.includeDays') }}:</span>
          <v-container class="ma-0 pa-0" max-width="425px">
            <v-row dense wrap>
              <v-col cols="4" class="ma-0 pa-0">
                <v-checkbox
                  :disabled="dayDisabled(1)"
                  :multiple="true"
                  :hide-details="true"
                  density="compact"
                  v-model="store.daysSelected"
                  :label="t('labels.days.monday')"
                  :value="1"
                ></v-checkbox>
              </v-col>
              <v-col cols="4" class="ma-0 pa-0">
                <v-checkbox
                  :disabled="dayDisabled(2)"
                  style="margin: 0; padding: 0"
                  :multiple="true"
                  :hide-details="true"
                  density="compact"
                  v-model="store.daysSelected"
                  :label="t('labels.days.tuesday')"
                  :value="2"
                ></v-checkbox>
              </v-col>
              <v-col cols="4" class="ma-0 pa-0">
                <v-checkbox
                  :disabled="dayDisabled(3)"
                  style="margin: 0; padding: 0"
                  :multiple="true"
                  :hide-details="true"
                  density="compact"
                  v-model="store.daysSelected"
                  :label="t('labels.days.wednesday')"
                  :value="3"
                ></v-checkbox>
              </v-col>
              <v-col cols="4" class="ma-0 pa-0">
                <v-checkbox
                  :disabled="dayDisabled(4)"
                  style="margin: 0; padding: 0"
                  :multiple="true"
                  :hide-details="true"
                  density="compact"
                  v-model="store.daysSelected"
                  :label="t('labels.days.thursday')"
                  :value="4"
                ></v-checkbox>
              </v-col>
              <v-col cols="4" class="ma-0 pa-0">
                <v-checkbox
                  :disabled="dayDisabled(5)"
                  style="margin: 0; padding: 0"
                  :multiple="true"
                  :hide-details="true"
                  density="compact"
                  v-model="store.daysSelected"
                  :label="t('labels.days.friday')"
                  :value="5"
                ></v-checkbox>
              </v-col>
              <v-col cols="4" class="ma-0 pa-0">
                <v-checkbox
                  :disabled="dayDisabled(6)"
                  style="margin: 0; padding: 0"
                  :multiple="true"
                  :hide-details="true"
                  density="compact"
                  v-model="store.daysSelected"
                  :label="t('labels.days.saturday')"
                  :value="6"
                ></v-checkbox>
              </v-col>
              <v-col cols="4" class="ma-0 pa-0">
                <v-checkbox
                  :disabled="dayDisabled(0)"
                  style="margin: 0; padding: 0"
                  :multiple="true"
                  :hide-details="true"
                  density="compact"
                  v-model="store.daysSelected"
                  :label="t('labels.days.sunday')"
                  :value="0"
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-container>
          <PrimaryComboBox
            v-if="store.mode === ManageSlotsMode.Create"
            required
            style="margin-top: 1rem"
            :rules="RulesManager.getRules(Rule.Required)"
            v-model="store.selectedAdminOption"
            @update:model-value="onAdministratorChange"
            @blur="onAdministratorBlur"
            :label="t('labels.administrator')"
            :items="store.administratorOptions"
          />
          <PrimaryComboBox
            v-if="store.mode === ManageSlotsMode.Create"
            required
            :rules="RulesManager.getRules(Rule.Required)"
            v-model="store.selectedLocation"
            :label="t('labels.location')"
            :items="store.locationValues"
          />
          <PrimaryComboBox
            required
            v-if="
              store.eventCategory === EventCategory.FitTest && store.mode === ManageSlotsMode.Create
            "
            :rules="RulesManager.getRules(Rule.Required)"
            v-model="store.selectedDeviceOption"
            :label="t('labels.slots.device')"
            :items="store.deviceOptions"
            @blur="onDeviceBlur"
          />
          <p v-if="store.timeRangeError" class="error-text">
            {{ t('errors.invalidTimeRange') }}
            <span v-if="store.mode === ManageSlotsMode.Create">{{
              t('errors.invalidTimeRangeCreate')
            }}</span
            >.
          </p>
        </v-col>
        <v-divider />
        <v-col class="buttons" cols="12">
          <ButtonPrimary
            v-if="store.mode === ManageSlotsMode.Clear"
            @click="clearSlots"
            :text="t('buttons.clearSlots')"
          />
          <ButtonPrimary
            v-if="store.mode === ManageSlotsMode.Create"
            @click="createSlots"
            :text="t('buttons.createSlots')"
          />
          <DialogSlotsConfirm
            v-model="store.showCreateEvents"
            :title="title"
            @close="hideCreateEvents"
          />
        </v-col>
      </v-row>
    </v-container>
  </v-form>
</template>

<script setup lang="ts">
// STORES, IMPORTS, & COMPOSABLES
import { useRulesManager } from '@/composables/RulesManager'
import { EventCategory } from '@/enums/EventCategory'
import { ManageSlotsMode } from '@/enums/ManageSlotsMode'
import { Rule } from '@/enums/Rule'
import { TableStoreId } from '@/enums/TableStoreId'
import type { IEventsStore } from '@/interfaces/api/IEventsStore'
import { TableStoreFactory } from '@/stores/db/TableStoreFactory'
import { useAppStore } from '@/stores/ui/AppStore'
import { useCalendarStore } from '@/stores/ui/CalendarStore'
import { useSlotsStore } from '@/stores/ui/SlotsStore'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const appStore = useAppStore()
const calendarStore = useCalendarStore()
const eventsStore = TableStoreFactory.get(TableStoreId.Events) as IEventsStore
const RulesManager = useRulesManager()
const store = useSlotsStore()
const { t } = useI18n({ useScope: 'global' })

// PROPS & EMITS

// REACTIVE VARIABLES
const manageSlotsForm = ref<any>()

// COMPUTED PROPERTIES
const title = computed(() => {
  let slugPrefix = store.mode as string
  switch (store.eventCategory) {
    case EventCategory.FitTest: {
      return t(`dialogs.headers.${slugPrefix}FitTestSlots`)
    }
    case EventCategory.MedicalClearance: {
      return t(`dialogs.headers.${slugPrefix}MedicalClearanceSlots`)
    }
    case EventCategory.Training: {
      return t(`dialogs.headers.${slugPrefix}TrainingSlots`)
    }
  }
  return t(`dialogs.headers.clearAllSlots`)
})

// LIFECYCLE HOOKS
onMounted(async () => {
  await store.loadOptions()
  if (appStore.currentAdmin?.id) {
    const currentUser = store.administratorOptions.find((option) => {
      return option.value === appStore.currentAdmin?.id
    })
    if (currentUser) {
      store.selectedAdminOption = currentUser
    }
  }
})

// FUNCTIONS
const clearSlots = async () => {
  const { valid } = await manageSlotsForm.value.validate()
  if (valid && !store.timeRangeError) {
    store.clearSlots()
    store.showCreateEvents = true
  }
}

const createSlots = async () => {
  const { valid } = await manageSlotsForm.value.validate()
  if (valid && !store.timeRangeError) {
    await store.createSlots()
    store.showCreateEvents = true
  }
}

const hideCreateEvents = () => {
  store.showCreateEvents = false
}

const dayDisabled = (day: number) => {
  return store.daysSelected.includes(day) && store.daysSelected.length <= 1
}

const onAdministratorBlur = () => {
  if (typeof store.selectedAdminOption !== 'object') {
    store.selectedAdminOption = null
  }
}

const onAdministratorChange = async () => {
  await store.loadAdministratorOptions()
}

const onDeviceBlur = () => {
  if (typeof store.selectedDeviceOption !== 'object') {
    store.selectedDeviceOption = null
  }
}

const onToggleChange = () => {
  if (store.mode === ManageSlotsMode.Create) {
    store.eventCategory = EventCategory.FitTest
  }
  if (store.mode === ManageSlotsMode.Clear) {
    store.eventCategory = EventCategory.All
  }
}
</script>

<style lang="scss" scoped>
.buttons {
  display: inline-flex;
}
.error-text {
  margin: 0;
  padding: 0;
  color: red;
}

.toggle-button {
  margin-top: 1rem;
}
</style>
